import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart } from 'lightweight-charts';
import s from './NovaTrading.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import { useNovaTheme } from '../NovaThemeContext';
import { notifySuccess } from '../../../utils/notify';
import { CandlestickChart, ChevronDown, X } from 'lucide-react';
import binance from '../../../img/binance-coin-bnb-logo-CD94CC6D31-seeklogo.com.png';
import bitcoin from '../../../img/Bitcoin.svg.png';
import ethereum from '../../../img/Ethereum-ETH-icon.png';
import usdclogo from '../../../img/usdclogo.png';
import solana from '../../../img/solana.png';

/**
 * NovaTrading — раздел «Торговля»: единый терминал в духе Binance.
 * Одна карточка, внутри разделённая hairline-ами на зоны:
 * шапка рынка / график + правая колонка (стакан + лента) / формы Купить-Продать /
 * нижние вкладки с ордерами.
 *
 * Вся «жизнь» биржи сохранена из оригинального TradingDashboard:
 *  — последняя свеча рисуется на +0.8…+1.3% выше реальной и колеблется
 *    каждые 2с с накоплением теней (кроме USDC);
 *  — книга ордеров: стартер анимаций каждые 150мс, прогресс каждые 80мс,
 *    исполненные ордера заменяются новыми с пересортировкой;
 *  — лента сделок: рандомное расписание (70% быстрых интервалов 200мс–1с).
 */

// Палитры графика под режимы Nova
const CHART_PALETTES = {
    dark: {
        text: '#78808F',
        grid: 'rgba(255, 255, 255, 0.06)',
        border: 'rgba(255, 255, 255, 0.10)',
        crosshair: 'rgba(166, 173, 187, 0.5)',
        up: '#3ECF8E',
        down: '#F06A6A',
        volUp: 'rgba(62, 207, 142, 0.30)',
        volDown: 'rgba(240, 106, 106, 0.30)',
    },
    light: {
        text: '#8A92A3',
        grid: 'rgba(16, 19, 26, 0.08)',
        border: 'rgba(16, 19, 26, 0.14)',
        crosshair: 'rgba(90, 98, 114, 0.5)',
        up: '#17A56D',
        down: '#D94C4C',
        volUp: 'rgba(23, 165, 109, 0.28)',
        volDown: 'rgba(217, 76, 76, 0.28)',
    },
};

// Таймфреймы графика (реально перезагружают свечи)
const TIMEFRAMES = [
    ['15m', '15m'],
    ['1H', '1h'],
    ['4H', '4h'],
    ['1D', '1d'],
    ['1W', '1w'],
];

const getCryptoIcon = (crypto) => {
    switch (crypto) {
        case 'BTC': return bitcoin;
        case 'ETH': return ethereum;
        case 'USDC': return usdclogo;
        case 'SOL': return solana;
        case 'BNB':
        default: return binance;
    }
};

export default function NovaTrading() {
    const { maxTotalMoney, bnbBalanca, selectedCrypto, siteSettings = {}, safeT } = useAppContext();
    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const { mode } = useNovaTheme();

    const crypto = selectedCrypto || 'BNB';
    const pair = `${crypto}/USD`;
    const currencyFormat = siteSettings.currencyFormat || 'USD';

    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);
    const volumeDataRef = useRef([]);
    const chartIntervalRef = useRef(null);
    const isChartMountedRef = useRef(false);
    const cryptoRef = useRef(crypto);
    cryptoRef.current = crypto;

    // Таймфрейм — реально влияет на загрузку свечей
    const [timeframe, setTimeframe] = useState('1H');
    const timeframeRef = useRef('1h');

    // Декоративные контролы «как на бирже»
    const [chartView, setChartView] = useState('original');
    const [marketTab, setMarketTab] = useState('spot');
    const [bottomTab, setBottomTab] = useState('open');

    const [orderType, setOrderType] = useState('limit');

    // Формы Купить / Продать — независимые
    const [buyPrice, setBuyPrice] = useState('');
    const [buyAmount, setBuyAmount] = useState('');
    const [buyPct, setBuyPct] = useState(0);
    const [sellPrice, setSellPrice] = useState('');
    const [sellAmount, setSellAmount] = useState('');
    const [sellPct, setSellPct] = useState(0);

    const [marketData, setMarketData] = useState({
        currentPrice: 0, priceChange: 0, high24h: 0, low24h: 0, volume24h: 0, quoteVolume24h: 0,
    });
    // OHLC текущей (фейковой) свечи — для легенды над графиком
    const [lastCandleInfo, setLastCandleInfo] = useState(null);

    const priceRef = useRef(0);
    useEffect(() => { priceRef.current = marketData.currentPrice; }, [marketData.currentPrice]);

    const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
    const [openOrders, setOpenOrders] = useState([]);
    const [tradeHistory, setTradeHistory] = useState([]);

    const formatNumber = useCallback((number, format = siteSettings.numberFormat || 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        const parts = parseFloat(number).toFixed(2).split('.');
        const [integerPart, decimalPart] = parts;
        if (format === 'dot') {
            return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimalPart}`;
        }
        return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`;
    }, [siteSettings.numberFormat]);

    // Раскраска объёмов зависит от темы — данные держим в ref
    const applyVolumeData = useCallback(() => {
        if (!volumeSeriesRef.current) return;
        const p = CHART_PALETTES[document.querySelector('[data-nova-mode]')?.getAttribute('data-nova-mode')] || CHART_PALETTES.dark;
        try {
            volumeSeriesRef.current.setData(volumeDataRef.current.map(v => ({
                time: v.time,
                value: v.value,
                color: v.up ? p.volUp : p.volDown,
            })));
        } catch (e) { /* размонтирование */ }
    }, []);

    // ── График: загрузка свечей + объёмы + трюк с последней свечой
    const fetchChartData = useCallback(async () => {
        if (!candleSeriesRef.current || !isChartMountedRef.current) return;
        try {
            const symbol = `${cryptoRef.current}USDT`;
            const interval = timeframeRef.current;
            const response = await fetch(`/api/binance/klines?symbol=${symbol}&interval=${interval}&limit=500`);
            const data = await response.json();
            if (!Array.isArray(data)) return;

            const cdata = data.slice(0, -1).map(d => ({
                time: d[0] / 1000,
                open: parseFloat(d[1]),
                high: parseFloat(d[2]),
                low: parseFloat(d[3]),
                close: parseFloat(d[4]),
            }));
            if (!candleSeriesRef.current || !isChartMountedRef.current) return;
            candleSeriesRef.current.setData(cdata);

            // Гистограмма объёмов под графиком
            volumeDataRef.current = data.map(d => ({
                time: d[0] / 1000,
                value: parseFloat(d[5]),
                up: parseFloat(d[4]) >= parseFloat(d[1]),
            }));
            applyVolumeData();

            const lastData = data[data.length - 1];

            if (cryptoRef.current === 'USDC') {
                // USDC — без манипуляций
                const candle = {
                    time: lastData[0] / 1000,
                    open: parseFloat(lastData[1]),
                    high: parseFloat(lastData[2]),
                    low: parseFloat(lastData[3]),
                    close: parseFloat(lastData[4]),
                };
                candleSeriesRef.current.update(candle);
                setLastCandleInfo(candle);
                setMarketData(prev => ({ ...prev, currentPrice: candle.close }));
                return;
            }

            // Трюк: close от +0.8% до +1.3% выше реального, тени накапливаются
            const open = parseFloat(lastData[1]);
            const realClose = parseFloat(lastData[4]);
            const realHigh = parseFloat(lastData[2]);
            const realLow = parseFloat(lastData[3]);
            const minIncrease = realClose * 1.008;
            const maxIncrease = realClose * 1.013;
            let fakeClose = minIncrease + (maxIncrease - minIncrease) * Math.random();
            let shadowHigh = Math.max(open, fakeClose, realHigh);
            let shadowLow = Math.min(open, fakeClose, realLow);
            const lastCandle = { time: lastData[0] / 1000, open, high: shadowHigh, low: shadowLow, close: fakeClose };

            candleSeriesRef.current.update(lastCandle);
            setLastCandleInfo(lastCandle);
            setMarketData(prev => ({ ...prev, currentPrice: fakeClose }));

            if (chartIntervalRef.current) clearInterval(chartIntervalRef.current);
            chartIntervalRef.current = setInterval(() => {
                if (!candleSeriesRef.current || !isChartMountedRef.current) {
                    clearInterval(chartIntervalRef.current);
                    return;
                }
                const newFakeClose = minIncrease + (maxIncrease - minIncrease) * Math.random();
                if (newFakeClose > shadowHigh) shadowHigh = newFakeClose;
                if (newFakeClose < shadowLow) shadowLow = newFakeClose;
                try {
                    const candle = {
                        time: lastCandle.time,
                        open: lastCandle.open,
                        high: shadowHigh,
                        low: shadowLow,
                        close: newFakeClose,
                    };
                    candleSeriesRef.current.update(candle);
                    setLastCandleInfo(candle);
                    setMarketData(prev => ({ ...prev, currentPrice: newFakeClose }));
                } catch (e) {
                    clearInterval(chartIntervalRef.current);
                }
            }, 2000);
        } catch (error) {
            console.error('Ошибка загрузки данных графика:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Инициализация графика — один раз
    useEffect(() => {
        const el = chartContainerRef.current;
        if (!el || isChartMountedRef.current) return;
        const p = CHART_PALETTES[mode] || CHART_PALETTES.dark;

        const chart = createChart(el, {
            width: el.clientWidth,
            height: el.clientHeight || 420,
            layout: {
                background: { color: 'transparent' },
                textColor: p.text,
                fontFamily: "'Inter', system-ui, sans-serif",
            },
            grid: {
                vertLines: { color: p.grid },
                horzLines: { color: p.grid },
            },
            crosshair: {
                mode: 'normal',
                vertLine: { color: p.crosshair, width: 1 },
                horzLine: { color: p.crosshair, width: 1 },
            },
            rightPriceScale: { borderColor: p.border, visible: true, textColor: p.text },
            timeScale: { borderColor: p.border, timeVisible: true, secondsVisible: false, textColor: p.text },
            handleScroll: { mouseWheel: true, pressedMouseMove: true },
            handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
        });
        const series = chart.addCandlestickSeries({
            upColor: p.up,
            downColor: p.down,
            borderUpColor: p.up,
            borderDownColor: p.down,
            wickUpColor: p.up,
            wickDownColor: p.down,
        });
        // Объёмы — тонкая гистограмма в нижней части, как на биржах
        const volSeries = chart.addHistogramSeries({
            priceFormat: { type: 'volume' },
            priceScaleId: 'vol',
            lastValueVisible: false,
            priceLineVisible: false,
        });
        chart.priceScale('vol').applyOptions({
            scaleMargins: { top: 0.84, bottom: 0 },
            visible: false,
        });

        chartRef.current = chart;
        candleSeriesRef.current = series;
        volumeSeriesRef.current = volSeries;
        isChartMountedRef.current = true;

        fetchChartData();

        // rAF обязателен: синхронный resize внутри колбэка зацикливает observer
        let resizeRaf = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(resizeRaf);
            resizeRaf = requestAnimationFrame(() => {
                if (!chartRef.current || !chartContainerRef.current) return;
                try {
                    chartRef.current.applyOptions({
                        width: chartContainerRef.current.clientWidth,
                        height: chartContainerRef.current.clientHeight,
                    });
                } catch (e) { /* размонтирование */ }
            });
        });
        ro.observe(el);

        return () => {
            ro.disconnect();
            cancelAnimationFrame(resizeRaf);
            if (chartIntervalRef.current) {
                clearInterval(chartIntervalRef.current);
                chartIntervalRef.current = null;
            }
            candleSeriesRef.current = null;
            volumeSeriesRef.current = null;
            try { chart.remove(); } catch (e) { /* уже удалён */ }
            chartRef.current = null;
            isChartMountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Перекраска при смене светлой/тёмной темы — без пересоздания
    useEffect(() => {
        if (!chartRef.current || !isChartMountedRef.current) return;
        const p = CHART_PALETTES[mode] || CHART_PALETTES.dark;
        try {
            chartRef.current.applyOptions({
                layout: { background: { color: 'transparent' }, textColor: p.text },
                grid: { vertLines: { color: p.grid }, horzLines: { color: p.grid } },
                crosshair: { vertLine: { color: p.crosshair }, horzLine: { color: p.crosshair } },
                rightPriceScale: { borderColor: p.border, textColor: p.text },
                timeScale: { borderColor: p.border, textColor: p.text },
            });
            candleSeriesRef.current?.applyOptions({
                upColor: p.up, downColor: p.down,
                borderUpColor: p.up, borderDownColor: p.down,
                wickUpColor: p.up, wickDownColor: p.down,
            });
            applyVolumeData();
        } catch (e) { /* график в процессе размонтирования */ }
    }, [mode, applyVolumeData]);

    // Смена криптовалюты: сброс и перезагрузка
    useEffect(() => {
        if (!isChartMountedRef.current) return;
        if (chartIntervalRef.current) clearInterval(chartIntervalRef.current);
        setMarketData({ currentPrice: 0, priceChange: 0, high24h: 0, low24h: 0, volume24h: 0, quoteVolume24h: 0 });
        setOrderBook({ bids: [], asks: [] });
        setLastCandleInfo(null);
        fetchChartData();
    }, [crypto, fetchChartData]);

    // Смена таймфрейма: перезагрузка свечей
    const handleTimeframe = (label, apiInterval) => {
        setTimeframe(label);
        timeframeRef.current = apiInterval;
        if (chartIntervalRef.current) clearInterval(chartIntervalRef.current);
        fetchChartData();
    };

    // Рыночная статистика с Binance каждые 5с
    useEffect(() => {
        const fetchMarketData = async () => {
            try {
                const symbol = `${crypto}USDT`;
                const response = await fetch(`/api/binance/ticker/24hr?symbol=${symbol}`);
                const statsData = await response.json();
                setMarketData(prev => ({
                    ...prev,
                    priceChange: parseFloat(statsData.priceChange),
                    high24h: parseFloat(statsData.highPrice),
                    low24h: parseFloat(statsData.lowPrice),
                    volume24h: parseFloat(statsData.volume),
                    quoteVolume24h: parseFloat(statsData.quoteVolume),
                    currentPrice: prev.currentPrice || parseFloat(statsData.lastPrice),
                }));
            } catch (error) { /* сеть недоступна */ }
        };
        fetchMarketData();
        const interval = setInterval(fetchMarketData, 5000);
        return () => clearInterval(interval);
    }, [crypto]);

    // ── Стакан: начальная генерация (6+6)
    const generateInitialOrderBook = useCallback((basePrice) => {
        const bids = [];
        const asks = [];
        for (let i = 0; i < 6; i++) {
            const bidPrice = basePrice - (0.1 + Math.random() * 0.2) * (i + 1);
            const askPrice = basePrice + (0.1 + Math.random() * 0.2) * (i + 1);
            const mk = (id, p) => {
                const amt = Math.random() * 50 + 10;
                return {
                    id: `${id}_${Date.now()}_${i}`,
                    price: p.toFixed(2),
                    amount: amt.toFixed(4),
                    total: (p * amt).toFixed(2),
                    fillProgress: 0, filled: false, isAnimating: false,
                    targetFill: 0, animationSpeed: 1, fillFrequency: 0.7,
                };
            };
            bids.push(mk('bid', bidPrice));
            asks.push(mk('ask', askPrice));
        }
        asks.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        bids.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        return { bids, asks };
    }, []);

    const generateOpenOrders = useCallback((basePrice) => {
        const orders = [];
        for (let i = 0; i < 4; i++) {
            orders.push({
                id: `ORD${Date.now()}${i}`,
                type: Math.random() > 0.5 ? 'Buy' : 'Sell',
                pair,
                price: (basePrice + (Math.random() - 0.5) * 20).toFixed(2),
                amount: (Math.random() * 10 + 1).toFixed(4),
                filled: Math.random() * 100,
                time: new Date(Date.now() - Math.random() * 3600000).toLocaleTimeString(),
            });
        }
        setOpenOrders(orders);
    }, [pair]);

    // Начальная история сделок — один раз
    useEffect(() => {
        const trades = [];
        const now = Date.now();
        const realPrice = priceRef.current || 621;
        for (let i = 0; i < 10; i++) {
            const tradePrice = realPrice + (Math.random() - 0.5) * 0.02 * realPrice;
            const secondsAgo = (i + 1) * (5 + Math.random() * 10);
            const tradeTime = new Date(now - secondsAgo * 1000);
            trades.push({
                id: `initial_trade_${i}`,
                time: tradeTime.toLocaleTimeString('en-US', { hour12: false }),
                price: tradePrice.toFixed(2),
                amount: (Math.random() * 5 + 0.1).toFixed(4),
                side: Math.random() > 0.5 ? 'buy' : 'sell',
                isNew: false,
                timestamp: tradeTime.getTime(),
            });
        }
        trades.sort((a, b) => b.timestamp - a.timestamp);
        setTradeHistory(trades);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Стакан и открытые ордера — при первом появлении цены
    useEffect(() => {
        if (marketData.currentPrice > 0) {
            setOrderBook(prev => (prev.asks.length === 0 || prev.bids.length === 0)
                ? generateInitialOrderBook(marketData.currentPrice)
                : prev);
            generateOpenOrders(marketData.currentPrice);
        }
    }, [marketData.currentPrice, generateInitialOrderBook, generateOpenOrders]);

    // Стартер анимаций заполнения (каждые 150мс, шанс 35%)
    useEffect(() => {
        const startAnimation = (order, advantageLevel) => {
            order.isAnimating = true;
            order.fillProgress = 0;
            if (advantageLevel === 'high') {
                order.animationSpeed = Math.random() * 2 + 4;
                order.targetFill = Math.random() * 20 + 80;
                order.fillFrequency = 0.9;
            } else if (advantageLevel === 'medium') {
                order.animationSpeed = Math.random() * 2 + 2;
                order.targetFill = Math.random() * 25 + 70;
                order.fillFrequency = 0.7;
            } else {
                order.animationSpeed = Math.random() * 1.5 + 0.5;
                order.targetFill = Math.random() * 30 + 60;
                order.fillFrequency = 0.4;
            }
            order.animationStartTime = Date.now();
        };

        const interval = setInterval(() => {
            setOrderBook(prev => {
                const next = { asks: [...prev.asks], bids: [...prev.bids] };
                next.asks.forEach((order, index) => {
                    if (Math.random() < 0.35 && !order.isAnimating) {
                        startAnimation(order, index >= 4 ? 'high' : index >= 2 ? 'medium' : 'low');
                    }
                });
                next.bids.forEach((order, index) => {
                    if (Math.random() < 0.35 && !order.isAnimating) {
                        startAnimation(order, index <= 1 ? 'high' : index <= 3 ? 'medium' : 'low');
                    }
                });
                return next;
            });
        }, 150);
        return () => clearInterval(interval);
    }, []);

    // Прогресс анимаций (каждые 80мс) + замена исполненных ордеров
    useEffect(() => {
        const interval = setInterval(() => {
            setOrderBook(prev => {
                const next = { asks: [...prev.asks], bids: [...prev.bids] };
                let hasAnimations = false;

                [...next.asks, ...next.bids].forEach(order => {
                    if (!order.isAnimating) return;
                    hasAnimations = true;

                    if (Math.random() < (order.fillFrequency || 0.7)) {
                        const jump = (Math.random() * 15 + 5) * ((order.animationSpeed || 2) / 2);
                        order.fillProgress = Math.min(order.fillProgress + jump, order.targetFill);
                    }

                    if (order.fillProgress >= order.targetFill) {
                        order.fillProgress = 100;
                        setTimeout(() => {
                            setOrderBook(current => {
                                const reset = { asks: [...current.asks], bids: [...current.bids] };
                                const isAsk = reset.asks.find(o => o.id === order.id);
                                const targetArray = isAsk ? reset.asks : reset.bids;
                                const orderIndex = targetArray.findIndex(o => o.id === order.id);
                                if (orderIndex !== -1) {
                                    const realCurrentPrice = priceRef.current || 621;
                                    const priceStep = (orderIndex + 1) * (0.1 + Math.random() * 0.2);
                                    const newPrice = isAsk ? realCurrentPrice + priceStep : realCurrentPrice - priceStep;
                                    const newAmount = Math.random() * 50 + 10;
                                    targetArray[orderIndex] = {
                                        id: `${isAsk ? 'ask' : 'bid'}_${Date.now()}_${Math.random()}`,
                                        price: newPrice.toFixed(2),
                                        amount: newAmount.toFixed(4),
                                        total: (newPrice * newAmount).toFixed(2),
                                        fillProgress: 0, filled: false, isAnimating: false,
                                        targetFill: 0, animationSpeed: 1, fillFrequency: 0.7,
                                    };
                                    targetArray.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
                                }
                                return reset;
                            });
                        }, Math.random() * 300 + 200);
                    }
                });

                return hasAnimations ? next : prev;
            });
        }, 80);
        return () => clearInterval(interval);
    }, []);

    // Лента сделок: рандомное расписание, 70% быстрых интервалов
    useEffect(() => {
        let tradesTimeout;
        const scheduleNextTrade = () => {
            const randomInterval = Math.random() < 0.7
                ? Math.random() * 800 + 200
                : Math.random() * 2000 + 1000;
            tradesTimeout = setTimeout(() => {
                setTradeHistory(prev => {
                    const base = priceRef.current || 0;
                    const tradePrice = base + (Math.random() - 0.5) * 0.01 * base;
                    const rand = Math.random();
                    let amt;
                    if (rand > 0.95) amt = Math.random() * 50 + 20;
                    else if (rand > 0.85) amt = Math.random() * 20 + 5;
                    else if (rand > 0.6) amt = Math.random() * 5 + 1;
                    else amt = Math.random() * 2 + 0.1;
                    const newTrade = {
                        id: `trade_${Date.now()}_${Math.random()}`,
                        time: new Date().toLocaleTimeString('en-US', { hour12: false }),
                        price: tradePrice.toFixed(2),
                        amount: amt.toFixed(4),
                        side: Math.random() > 0.5 ? 'buy' : 'sell',
                        isNew: true,
                        timestamp: Date.now(),
                    };
                    return [newTrade, ...prev.slice(0, 9)];
                });
                scheduleNextTrade();
            }, randomInterval);
        };
        scheduleNextTrade();
        return () => clearTimeout(tradesTimeout);
    }, []);

    const handlePlaceOrder = (side, amount, price) => {
        notifySuccess(`${side.toUpperCase()} order placed: ${amount} ${crypto} at $${price}`);
    };

    // Слайдер процентов: пересчитывает количество от доступного баланса
    const applyPct = (side, pct) => {
        const priceValue = side === 'buy'
            ? (parseFloat(buyPrice) || marketData.currentPrice)
            : (parseFloat(sellPrice) || marketData.currentPrice);
        if (side === 'buy') {
            setBuyPct(pct);
            if (priceValue) setBuyAmount(((maxTotalMoney * pct / 100) / priceValue).toFixed(4));
        } else {
            setSellPct(pct);
            const cryptoBalance = parseFloat(bnbBalanca) || 0;
            setSellAmount((cryptoBalance * pct / 100).toFixed(4));
        }
    };

    const changePct = marketData.currentPrice
        ? ((marketData.priceChange / marketData.currentPrice) * 100).toFixed(2)
        : '0.00';
    const changePositive = marketData.priceChange >= 0;

    // Изменение текущей свечи — для OHLC-легенды
    const candleChangePct = lastCandleInfo && lastCandleInfo.open
        ? (((lastCandleInfo.close - lastCandleInfo.open) / lastCandleInfo.open) * 100).toFixed(2)
        : null;
    const candleUp = lastCandleInfo ? lastCandleInfo.close >= lastCandleInfo.open : true;

    // Соотношение покупок/продаж в стакане — как индикатор глубины Binance
    const bidSum = orderBook.bids.reduce((acc, o) => acc + parseFloat(o.amount), 0);
    const askSum = orderBook.asks.reduce((acc, o) => acc + parseFloat(o.amount), 0);
    const buyRatio = bidSum + askSum > 0 ? (bidSum / (bidSum + askSum)) * 100 : 50;

    const stats = [
        { label: t('tradingDashboard.stats.change24h', 'Изменение 24ч'), value: `${changePositive ? '+' : ''}${formatNumber(marketData.priceChange)} ${changePositive ? '+' : ''}${changePct}%`, tone: changePositive ? 'up' : 'down' },
        { label: t('tradingDashboard.stats.high24h', 'Макс. 24ч'), value: formatNumber(marketData.high24h) },
        { label: t('tradingDashboard.stats.low24h', 'Мин. 24ч'), value: formatNumber(marketData.low24h) },
        { label: `${t('tradingDashboard.stats.volume24h', 'Объём 24ч')} (${crypto})`, value: formatNumber(marketData.volume24h).replace(/[.,]\d\d$/, '') },
        { label: `${t('tradingDashboard.stats.volume24h', 'Объём 24ч')} (USD)`, value: formatNumber(marketData.quoteVolume24h).replace(/[.,]\d\d$/, '') },
    ];

    const bottomTabs = [
        ['open', `${t('tradingDashboard.sections.openOrders', 'Открытые ордера')} (${openOrders.length})`],
        ['orderHistory', t('tradingDashboard.tabs.orderHistory', 'История ордеров')],
        ['tradeHistory', t('tradingDashboard.tabs.tradeHistory', 'История сделок')],
        ['funds', t('tradingDashboard.tabs.funds', 'Средства')],
    ];

    const renderForm = (side) => {
        const isBuy = side === 'buy';
        const price = isBuy ? buyPrice : sellPrice;
        const setPrice = isBuy ? setBuyPrice : setSellPrice;
        const amount = isBuy ? buyAmount : sellAmount;
        const setAmount = isBuy ? setBuyAmount : setSellAmount;
        const pct = isBuy ? buyPct : sellPct;
        const total = (parseFloat(price) || marketData.currentPrice) * parseFloat(amount || 0);
        const avbl = isBuy
            ? `${currencyFormat} ${formatNumber(maxTotalMoney)}`
            : `${parseFloat(bnbBalanca) ? parseFloat(bnbBalanca).toFixed(4) : '—'} ${crypto}`;

        return (
            <div className={s.form}>
                <div className={s.avblRow}>
                    <span>{t('tradingDashboard.summary.available', 'Доступно')}</span>
                    <span className={s.avblValue}>{avbl}</span>
                </div>
                {orderType !== 'market' && (
                    <label className={s.field}>
                        <span className={s.fieldLabel}>{t('tradingDashboard.form.priceLabel', 'Цена (USD)')}</span>
                        <input
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className={s.fieldInput}
                            step="0.01"
                            placeholder={marketData.currentPrice ? marketData.currentPrice.toFixed(2) : '0.00'}
                        />
                        <span className={s.fieldUnit}>USD</span>
                    </label>
                )}
                <label className={s.field}>
                    <span className={s.fieldLabel}>{t('tradingDashboard.form.amountLabel', 'Количество')}</span>
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className={s.fieldInput}
                        step="0.0001"
                        placeholder="0.0000"
                    />
                    <span className={s.fieldUnit}>{crypto}</span>
                </label>
                <div className={s.sliderRow}>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="25"
                        value={pct}
                        onChange={(e) => applyPct(side, parseInt(e.target.value, 10))}
                        className={`${s.slider} ${isBuy ? s.sliderBuy : s.sliderSell}`}
                        aria-label="%"
                    />
                    <span className={s.sliderValue}>{pct}%</span>
                </div>
                <div className={s.totalRow}>
                    <span>{t('tradingDashboard.summary.total', 'Итого')}</span>
                    <span className={s.totalValue}>${formatNumber(total)}</span>
                </div>
                <button
                    type="button"
                    className={`${s.submit} ${isBuy ? s.submitBuy : s.submitSell}`}
                    onClick={() => handlePlaceOrder(side, amount, price || marketData.currentPrice.toFixed(2))}
                    disabled={!amount || (orderType !== 'market' && !price)}
                >
                    {isBuy
                        ? t('tradingDashboard.sides.buy', 'Купить')
                        : t('tradingDashboard.sides.sell', 'Продать')} {crypto}
                </button>
            </div>
        );
    };

    return (
        <div className={s.page}>
            <section className={s.terminalWrap}>
                <span className={s.tab}>
                    <CandlestickChart size={12} strokeWidth={2} aria-hidden="true" />
                    {t('header.trade', 'Торговля')}
                </span>

                <div className={s.terminal}>
                    {/* ── 1. Шапка рынка */}
                    <div className={s.marketBar}>
                        <div className={s.pairBlock}>
                            <img src={getCryptoIcon(crypto)} alt="" className={s.pairIcon} />
                            <div className={s.pairMeta}>
                                <span className={s.pairName}>{pair}</span>
                                <span className={s.pairLive}>
                                    <span className={s.liveDot} aria-hidden="true" />
                                    Binance
                                </span>
                            </div>
                        </div>
                        <div className={s.priceBlock}>
                            <span className={`${s.currentPrice} ${changePositive ? s.up : s.down}`}>
                                ${formatNumber(marketData.currentPrice)}
                            </span>
                        </div>
                        <div className={s.stats}>
                            {stats.map((stat, i) => (
                                <div key={i} className={s.stat}>
                                    <span className={s.statLabel}>{stat.label}</span>
                                    <span className={`${s.statValue} ${stat.tone === 'up' ? s.up : stat.tone === 'down' ? s.down : ''}`}>
                                        {stat.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── 2. График + правая колонка (стакан + лента) */}
                    <div className={s.mainSplit}>
                        <div className={s.chartZone}>
                            <div className={s.chartTools}>
                                <div className={s.timeframes}>
                                    {TIMEFRAMES.map(([label, api]) => (
                                        <button
                                            key={label}
                                            type="button"
                                            className={`${s.tfBtn} ${timeframe === label ? s.tfActive : ''}`}
                                            onClick={() => handleTimeframe(label, api)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className={s.chartViews}>
                                    {[
                                        ['original', 'Original'],
                                        ['tv', 'Trading View'],
                                        ['depth', 'Depth'],
                                    ].map(([key, label]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            className={`${s.viewBtn} ${chartView === key ? s.viewActive : ''}`}
                                            onClick={() => setChartView(key)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {lastCandleInfo && (
                                <div className={s.ohlcRow}>
                                    <span className={s.ohlcItem}>O <b className={candleUp ? s.up : s.down}>{lastCandleInfo.open.toFixed(2)}</b></span>
                                    <span className={s.ohlcItem}>H <b className={candleUp ? s.up : s.down}>{lastCandleInfo.high.toFixed(2)}</b></span>
                                    <span className={s.ohlcItem}>L <b className={candleUp ? s.up : s.down}>{lastCandleInfo.low.toFixed(2)}</b></span>
                                    <span className={s.ohlcItem}>C <b className={candleUp ? s.up : s.down}>{lastCandleInfo.close.toFixed(2)}</b></span>
                                    {candleChangePct !== null && (
                                        <span className={`${s.ohlcChange} ${candleUp ? s.up : s.down}`}>
                                            {candleUp ? '+' : ''}{candleChangePct}%
                                        </span>
                                    )}
                                    <span className={s.maLegend}>
                                        <span className={s.ma7}>MA(7): {(lastCandleInfo.close * 0.997).toFixed(2)}</span>
                                        <span className={s.ma25}>MA(25): {(lastCandleInfo.close * 0.992).toFixed(2)}</span>
                                        <span className={s.ma99}>MA(99): {(lastCandleInfo.close * 0.984).toFixed(2)}</span>
                                    </span>
                                </div>
                            )}
                            <div ref={chartContainerRef} className={s.chartContainer} />
                        </div>

                        <div className={s.rightRail}>
                            {/* Книга ордеров */}
                            <div className={s.bookZone}>
                                <div className={s.zoneHead}>
                                    <span className={s.zoneTitle}>{t('tradingDashboard.sections.orderBook', 'Книга ордеров')}</span>
                                    <button type="button" className={s.precisionChip}>
                                        0.01
                                        <ChevronDown size={11} strokeWidth={2} aria-hidden="true" />
                                    </button>
                                </div>
                                <div className={s.bookHead}>
                                    <span>{t('tradingDashboard.table.price', 'Цена')} (USD)</span>
                                    <span>{t('tradingDashboard.table.amount', 'Кол-во')} ({crypto})</span>
                                    <span>{t('tradingDashboard.summary.total', 'Итого')}</span>
                                </div>
                                <div className={s.bookRows}>
                                    {orderBook.asks.slice(0, 6).reverse().map((ask) => (
                                        <div key={ask.id} className={s.bookRow}>
                                            <div
                                                className={`${s.fillBar} ${s.fillAsk}`}
                                                style={{ width: `${ask.fillProgress || 0}%`, opacity: ask.isAnimating ? 1 : 0 }}
                                            />
                                            <span className={s.down}>{ask.price}</span>
                                            <span>{ask.amount}</span>
                                            <span>{ask.total}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className={s.midPrice}>
                                    <span className={`${s.midPriceValue} ${changePositive ? s.up : s.down}`}>
                                        ${marketData.currentPrice.toFixed(2)}
                                    </span>
                                    <span className={s.midPriceSub}>≈ ${marketData.currentPrice.toFixed(2)}</span>
                                </div>
                                <div className={s.bookRows}>
                                    {orderBook.bids.slice(0, 6).map((bid) => (
                                        <div key={bid.id} className={s.bookRow}>
                                            <div
                                                className={`${s.fillBar} ${s.fillBid}`}
                                                style={{ width: `${bid.fillProgress || 0}%`, opacity: bid.isAnimating ? 1 : 0 }}
                                            />
                                            <span className={s.up}>{bid.price}</span>
                                            <span>{bid.amount}</span>
                                            <span>{bid.total}</span>
                                        </div>
                                    ))}
                                </div>
                                {/* Соотношение покупок/продаж, как на Binance */}
                                <div className={s.depthRatio}>
                                    <span className={s.up}>B {buyRatio.toFixed(2)}%</span>
                                    <span className={s.ratioTrack} aria-hidden="true">
                                        <span className={s.ratioBuy} style={{ width: `${buyRatio}%` }} />
                                    </span>
                                    <span className={s.down}>{(100 - buyRatio).toFixed(2)}% S</span>
                                </div>
                            </div>

                            {/* Последние сделки */}
                            <div className={s.tradesZone}>
                                <div className={s.zoneHead}>
                                    <span className={s.zoneTitle}>{t('tradingDashboard.sections.recentTrades', 'Последние сделки')}</span>
                                </div>
                                <div className={s.bookHead}>
                                    <span>{t('tradingDashboard.table.price', 'Цена')} (USD)</span>
                                    <span>{t('tradingDashboard.table.amount', 'Кол-во')} ({crypto})</span>
                                    <span>{t('tradingDashboard.table.time', 'Время')}</span>
                                </div>
                                <div className={s.tradesRows}>
                                    {tradeHistory.slice(0, 10).map((trade) => (
                                        <div key={trade.id} className={`${s.tradeRow} ${trade.isNew ? s.tradeNew : ''}`}>
                                            <span className={trade.side === 'buy' ? s.up : s.down}>{trade.price}</span>
                                            <span>{trade.amount}</span>
                                            <span className={s.tradeTime}>{trade.time}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── 3. Формы Купить / Продать */}
                    <div className={s.formsZone}>
                        <div className={s.formsHead}>
                            <div className={s.marketTabs}>
                                {[
                                    ['spot', 'Spot'],
                                    ['cross', 'Cross 5x'],
                                    ['isolated', 'Isolated'],
                                    ['grid', 'Grid'],
                                ].map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`${s.marketTab} ${marketTab === key ? s.marketTabActive : ''}`}
                                        onClick={() => setMarketTab(key)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div className={s.orderTypes}>
                                {[
                                    ['limit', t('tradingDashboard.orderTypes.limit', 'Лимит')],
                                    ['market', t('tradingDashboard.orderTypes.market', 'Рынок')],
                                    ['stop', t('tradingDashboard.orderTypes.stop', 'Стоп')],
                                ].map(([key, label]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`${s.orderTypeBtn} ${orderType === key ? s.orderTypeActive : ''}`}
                                        onClick={() => setOrderType(key)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={s.formsSplit}>
                            {renderForm('buy')}
                            <div className={s.formsDivider} aria-hidden="true" />
                            {renderForm('sell')}
                        </div>
                    </div>

                    {/* ── 4. Нижние вкладки */}
                    <div className={s.bottomZone}>
                        <div className={s.bottomTabs} role="tablist">
                            {bottomTabs.map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    role="tab"
                                    aria-selected={bottomTab === key}
                                    className={`${s.bottomTab} ${bottomTab === key ? s.bottomTabActive : ''}`}
                                    onClick={() => setBottomTab(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {bottomTab === 'open' ? (
                            <div className={s.ordersTable}>
                                <div className={s.ordersHead}>
                                    <span>{t('tradingDashboard.table.type', 'Тип')}</span>
                                    <span>{t('tradingDashboard.table.pair', 'Пара')}</span>
                                    <span>{t('tradingDashboard.table.price', 'Цена')}</span>
                                    <span>{t('tradingDashboard.table.amount', 'Кол-во')}</span>
                                    <span>{t('tradingDashboard.table.filled', 'Исполнено')}</span>
                                    <span>{t('tradingDashboard.table.time', 'Время')}</span>
                                    <span className={s.cellRight}>{t('tradingDashboard.table.action', 'Действие')}</span>
                                </div>
                                {openOrders.length > 0 ? openOrders.map(order => (
                                    <div key={order.id} className={s.orderRow}>
                                        <span>
                                            <span className={`${s.sideBadge} ${order.type === 'Buy' ? s.upBadge : s.downBadge}`}>
                                                {order.type === 'Buy'
                                                    ? t('tradingDashboard.sides.buy', 'Купить')
                                                    : t('tradingDashboard.sides.sell', 'Продать')}
                                            </span>
                                        </span>
                                        <span>{order.pair}</span>
                                        <span>${formatNumber(order.price)}</span>
                                        <span>{order.amount}</span>
                                        <span className={s.filledCell}>
                                            <span className={s.filledTrack}>
                                                <span className={s.filledBar} style={{ width: `${order.filled}%` }} />
                                            </span>
                                            {order.filled.toFixed(1)}%
                                        </span>
                                        <span className={s.tradeTime}>{order.time}</span>
                                        <span className={s.cellRight}>
                                            <button type="button" className={s.cancelBtn}>
                                                <X size={11} strokeWidth={2.2} aria-hidden="true" />
                                                {t('tradingDashboard.buttons.cancel', 'Отменить')}
                                            </button>
                                        </span>
                                    </div>
                                )) : (
                                    <div className={s.empty}>{t('tradingDashboard.emptyState.noOpenOrders', 'Нет открытых ордеров')}</div>
                                )}
                            </div>
                        ) : (
                            <div className={s.empty}>{t('tradingDashboard.emptyState.noData', 'Нет данных')}</div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
